/**
 * One icon on the Desktop — a file, a folder, a system folder (Arche Saves,
 * My-Fias) or the Trash — absolutely positioned where its canvas says.
 *
 * Drawn three ways, by the view:
 *   - `tile`    the icon (or an image's thumbnail) with the name below;
 *   - `inline`  a small icon with the name beside it (small icons, List);
 *   - `details` a row of the Details table: name, date modified, type, size.
 *
 * Pointer handling starts drags (after a small move with a mouse, or a
 * 350 ms press on touch), selects on click, and opens on double-click /
 * double-tap (with Ctrl/Cmd held: in a new window). Keyboard handling lives
 * on the canvas (one roving tab stop); the icon just renders what it's told.
 */
import { useEffect, useRef, useState } from 'react';
import { FolderClosed, Loader2, Lock, PackageOpen, Settings2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatBytes,
  formatModified,
  iconForMime,
  typeLabel,
} from '@/components/files/file-dialog/format';
import { scrubForDisplay } from '@/components/files/file-dialog/display-safety';
import { isImageMime } from '../../lib/file-display';
import type { DesktopStyle, ViewMetrics } from '../../lib/folder-view';
import { DocumentThumbnail } from '../shared/DocumentThumbnail';
import { VisibilityBadge } from '../VisibilityBadge';
import type { DesktopItem } from './desktop-model';

export type IconVariant = 'tile' | 'inline' | 'details';

/** The Details table's columns after the name — shared with its header. */
export const DETAILS_COLUMNS = 'minmax(0,1fr) 9.5rem 9rem 5.5rem';

/** Movement (px) before a mouse press becomes a drag. */
const DRAG_THRESHOLD = 4;
/** Press length (ms) before a touch becomes a drag. */
const LONG_PRESS_MS = 350;
/** Two taps within this (ms) open. */
const DOUBLE_TAP_MS = 300;

export interface DesktopIconProps {
  item: DesktopItem;
  container: string;
  /** Where it's drawn, in canvas px. */
  box: { x: number; y: number; w: number; h: number };
  variant: IconVariant;
  metrics: ViewMetrics;
  style: DesktopStyle;
  selected: boolean;
  focused: boolean;
  /** The canvas's single tab stop. */
  tabbable: boolean;
  /** A move or delete is in flight. */
  pending: boolean;
  /** Being dragged (drawn faint in place; a ghost follows the pointer). */
  dragging: boolean;
  /** Cut to the clipboard. */
  cut: boolean;
  /** Folder icon currently under a drag, and whether it would accept it. */
  dropHover: 'allowed' | 'refused' | null;
  renaming: boolean;
  onSelect: (mode: 'replace' | 'toggle' | 'range') => void;
  /** `newWindow`: Ctrl/Cmd was held (a folder opens in a new window). */
  onOpen: (options?: { newWindow?: boolean }) => void;
  onFocus: () => void;
  onStartDrag: (pointer: { x: number; y: number }, grab: { dx: number; dy: number }) => void;
  onContextMenu: (pointer: { x: number; y: number }, el: HTMLElement) => void;
  onRename: (name: string) => void;
  onCancelRename: () => void;
}

export function ItemGlyph({
  item,
  size = 'lg',
}: {
  item: DesktopItem;
  size?: 'lg' | 'sm' | number;
}) {
  const cls = size === 'lg' ? 'h-11 w-11' : size === 'sm' ? 'h-5 w-5' : 'shrink-0';
  const px = typeof size === 'number' ? Math.round(size * 0.92) : undefined;
  const dims = px ? { width: px, height: px } : undefined;
  if (item.kind === 'trash') {
    return (
      <span className="relative inline-flex">
        <Trash2 className={cn(cls, 'text-muted-foreground')} style={dims} aria-hidden />
        {item.full && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary" />
        )}
      </span>
    );
  }
  if (item.kind === 'folder') {
    if (item.system === 'arche-saves')
      return <PackageOpen className={cn(cls, 'text-primary')} style={dims} aria-hidden />;
    if (item.system === 'my-fias')
      return <Settings2 className={cn(cls, 'text-muted-foreground')} style={dims} aria-hidden />;
    return (
      <FolderClosed
        className={cn(cls, item.folder.isProtected ? 'text-muted-foreground' : 'text-primary/80')}
        style={dims}
        aria-hidden
      />
    );
  }
  const Icon = iconForMime(item.doc.mimeType);
  return <Icon className={cn(cls, 'text-muted-foreground')} style={dims} aria-hidden />;
}

/** The Details row's cells after the name. */
function detailsCells(item: DesktopItem, style: DesktopStyle) {
  if (item.kind === 'file') {
    return {
      modified: formatModified(item.doc.updatedAt),
      type: typeLabel(item.name, item.doc.mimeType),
      size: formatBytes(item.doc.sizeBytes),
    };
  }
  if (item.kind === 'folder') {
    return {
      modified: item.folder.updatedAt ? formatModified(item.folder.updatedAt) : '',
      type: style === 'mac' ? 'Folder' : 'File folder',
      size: '—',
    };
  }
  return { modified: '', type: 'Trash', size: '—' };
}

/** "sunset.png — made by Image Generator" style description for tooltips. */
function describe(item: DesktopItem): string {
  if (item.kind === 'trash') return item.full ? 'Trash (has files)' : 'Trash (empty)';
  if (item.kind === 'folder') {
    if (item.system === 'arche-saves') return 'Arche Saves — files your arches delivered';
    if (item.system === 'my-fias') return 'My-Fias — files your arches keep for themselves';
    return item.folder.isProtected ? `${item.name} (managed by Fias)` : item.name;
  }
  const maker = item.doc.sourceArcheName
    ? ` — made by ${scrubForDisplay(item.doc.sourceArcheName, 60)}`
    : '';
  return `${item.name}${maker}`;
}

export function DesktopIcon(props: DesktopIconProps) {
  const { item, container, box, variant, metrics, style, selected, focused, renaming } = props;
  const ref = useRef<HTMLDivElement>(null);
  const press = useRef<{
    x: number;
    y: number;
    dx: number;
    dy: number;
    pointerType: string;
    timer: ReturnType<typeof setTimeout> | null;
    dragged: boolean;
  } | null>(null);
  const lastTap = useRef(0);

  useEffect(() => {
    if (focused && !renaming) ref.current?.focus({ preventScroll: false });
  }, [focused, renaming]);

  useEffect(
    () => () => {
      if (press.current?.timer) clearTimeout(press.current.timer);
    },
    [],
  );

  const startDrag = (x: number, y: number) => {
    const p = press.current;
    if (!p || p.dragged) return;
    p.dragged = true;
    if (p.timer) clearTimeout(p.timer);
    props.onStartDrag({ x, y }, { dx: p.dx, dy: p.dy });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (renaming || e.button !== 0) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    press.current = {
      x: e.clientX,
      y: e.clientY,
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      pointerType: e.pointerType,
      timer: null,
      dragged: false,
    };
    if (e.pointerType === 'touch') {
      const x = e.clientX;
      const y = e.clientY;
      press.current.timer = setTimeout(() => startDrag(x, y), LONG_PRESS_MS);
    }
    const move = (ev: PointerEvent) => {
      const p = press.current;
      if (!p || p.dragged) return;
      const distance = Math.hypot(ev.clientX - p.x, ev.clientY - p.y);
      if (p.pointerType === 'touch') {
        // A touch that moves before the long press is a swipe, not a drag.
        if (distance > 8 && p.timer) {
          clearTimeout(p.timer);
          p.timer = null;
        }
        return;
      }
      if (distance > DRAG_THRESHOLD) startDrag(ev.clientX, ev.clientY);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      const p = press.current;
      if (p?.timer) clearTimeout(p.timer);
      press.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (renaming) return;
    const isTouch = (e.nativeEvent as PointerEvent).pointerType === 'touch';
    if (isTouch) {
      const now = Date.now();
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        lastTap.current = 0;
        props.onOpen();
        return;
      }
      lastTap.current = now;
    }
    props.onSelect(e.shiftKey ? 'range' : e.ctrlKey || e.metaKey ? 'toggle' : 'replace');
  };

  return (
    <div
      ref={ref}
      role="option"
      aria-selected={selected}
      aria-label={describe(item)}
      tabIndex={props.tabbable ? 0 : -1}
      data-desktop-key={item.key}
      data-desktop-container={container}
      {...(item.kind === 'folder'
        ? { 'data-desktop-drop': `folder:${item.key}` }
        : item.kind === 'trash'
          ? { 'data-desktop-drop': 'trash' }
          : {})}
      title={describe(item)}
      onPointerDown={onPointerDown}
      onClick={onClick}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!renaming) props.onOpen({ newWindow: e.ctrlKey || e.metaKey });
      }}
      onFocus={props.onFocus}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        props.onContextMenu({ x: e.clientX, y: e.clientY }, e.currentTarget);
      }}
      onDragStart={(e) => e.preventDefault()}
      className={cn(
        'absolute select-none outline-none transition-[background-color,opacity,box-shadow]',
        'focus-visible:ring-2 focus-visible:ring-ring',
        variant === 'tile' && 'flex flex-col items-center gap-1 rounded-md p-1.5 text-center',
        variant === 'inline' && 'flex items-center gap-2 rounded px-1.5',
        variant === 'details' && 'grid items-center gap-3 px-3 text-sm',
        // Windows highlights the whole item; a Mac highlights the icon and
        // puts the name in a blue pill (drawn on the label below).
        selected
          ? style === 'mac' && variant === 'tile'
            ? ''
            : style === 'mac'
              ? 'bg-[#0a64d6] text-white'
              : 'bg-primary/15 ring-1 ring-primary/50'
          : 'hover:bg-muted/60',
        variant === 'details' && !selected && 'odd:bg-transparent',
        props.dragging && 'opacity-40',
        props.cut && 'opacity-60',
        props.pending && 'opacity-60',
        props.dropHover === 'allowed' && 'bg-primary/20 ring-2 ring-primary',
        props.dropHover === 'refused' && 'ring-2 ring-destructive/60',
      )}
      style={{
        left: box.x,
        top: box.y,
        width: box.w,
        height: box.h,
        touchAction: 'none',
        cursor: renaming ? 'text' : 'default',
        ...(variant === 'details' ? { gridTemplateColumns: DETAILS_COLUMNS } : null),
      }}
    >
      {variant === 'details' ? (
        <>
          <span className="flex min-w-0 items-center gap-2">
            <IconFace item={item} px={metrics.icon} thumbnails={false} pending={props.pending} />
            {renaming ? (
              <RenameField
                initial={item.name}
                onCommit={props.onRename}
                onCancel={props.onCancelRename}
              />
            ) : (
              <span className="truncate">{item.name}</span>
            )}
          </span>
          <DetailsCells item={item} style={style} selected={selected && style === 'mac'} />
        </>
      ) : (
        <>
          <IconFace
            item={item}
            px={metrics.icon}
            thumbnails={metrics.thumbnails}
            pending={props.pending}
            highlight={selected && style === 'mac' && variant === 'tile'}
          />
          {renaming ? (
            <RenameField
              initial={item.name}
              onCommit={props.onRename}
              onCancel={props.onCancelRename}
            />
          ) : (
            <span
              className={cn(
                variant === 'tile'
                  ? 'line-clamp-2 max-w-full break-words text-[11px] leading-tight'
                  : 'min-w-0 truncate text-xs',
                variant === 'tile' && metrics.icon >= 96 && 'text-xs',
                selected && style === 'mac' && variant === 'tile'
                  ? 'rounded bg-[#0a64d6] px-1 text-white'
                  : selected
                    ? ''
                    : 'text-foreground/90',
              )}
            >
              {item.name}
            </span>
          )}
        </>
      )}
    </div>
  );
}

/** The icon, an image's thumbnail, or a glyph — with its status marks. */
function IconFace({
  item,
  px,
  thumbnails,
  pending,
  highlight = false,
}: {
  item: DesktopItem;
  px: number;
  thumbnails: boolean;
  pending: boolean;
  /** Mac selection: a soft rounded box behind the icon. */
  highlight?: boolean;
}) {
  const small = px <= 20;
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden',
        small ? 'rounded-sm' : 'rounded',
        highlight && 'bg-black/10 dark:bg-white/15',
      )}
      style={{ width: px, height: px }}
    >
      {item.kind === 'file' && thumbnails && isImageMime(item.doc.mimeType) ? (
        <DocumentThumbnail
          documentId={item.doc.documentId}
          name={item.doc.name}
          iconClassName={small ? 'h-4 w-4' : 'h-10 w-10'}
          fit={px >= 96 ? 'contain' : 'cover'}
        />
      ) : (
        <ItemGlyph item={item} size={px} />
      )}
      {pending && (
        <span className="absolute inset-0 flex items-center justify-center bg-background/60">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
        </span>
      )}
      {!small && item.kind === 'file' && item.doc.visibility === 'arche-private' && (
        <VisibilityBadge visibility="arche-private" compact className="absolute bottom-0 right-0" />
      )}
      {!small && item.kind === 'folder' && item.folder.isProtected && !item.system && (
        <Lock
          className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-background text-muted-foreground"
          aria-hidden
        />
      )}
    </div>
  );
}

function DetailsCells({
  item,
  style,
  selected,
}: {
  item: DesktopItem;
  style: DesktopStyle;
  selected: boolean;
}) {
  const cells = detailsCells(item, style);
  const tone = selected ? 'text-white/85' : 'text-muted-foreground';
  return (
    <>
      <span className={cn('truncate text-xs', tone)}>{cells.modified}</span>
      <span className={cn('truncate text-xs', tone)}>{cells.type}</span>
      <span className={cn('truncate text-right text-xs tabular-nums', tone)}>{cells.size}</span>
    </>
  );
}

function RenameField({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // Select the name without its extension, as a file manager does.
    const dot = initial.lastIndexOf('.');
    input.setSelectionRange(0, dot > 0 ? dot : initial.length);
  }, [initial]);
  const finish = (commit: boolean) => {
    if (done.current) return;
    done.current = true;
    if (commit && value.trim() && value.trim() !== initial) onCommit(value.trim());
    else onCancel();
  };
  return (
    <input
      ref={inputRef}
      aria-label="New name"
      value={value}
      maxLength={255}
      onChange={(e) => setValue(e.target.value)}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') finish(true);
        else if (e.key === 'Escape') finish(false);
      }}
      onBlur={() => finish(true)}
      className="w-full rounded border border-primary bg-background px-1 text-[11px] leading-tight outline-none"
    />
  );
}
